import pickBy from 'lodash/pickBy'
import {
  GeckoHorizontalWeld,
  GeckoHorizontalWeldJSON,
  GeckoRectangularManway,
  GeckoRectangularManwayJSON,
  GeckoVerticalWeld,
  GeckoVerticalWeldJSON,
} from 'types'
import {
  GeckoComponentModelType,
  GeckoFeatureModelType,
} from 'types/assetModel/assetModelEnums'
import {
  compassOrientationFormField,
  tminFormField,
} from 'types/assetModel/commonFields'
import {
  GeckoEditableComponent,
  SceneConfig,
} from 'types/assetModel/components/geckoEditableComponent'
import {
  GeckoManway,
  GeckoManwayJSON,
} from 'types/assetModel/features/geckoManway'
import {
  GeckoNozzle,
  GeckoNozzleJSON,
} from 'types/assetModel/features/geckoNozzle'
import {
  GeckoPlate,
  GeckoPlateJSON,
} from 'types/assetModel/features/geckoPlate'
import { weightThicknessValue } from 'types/assetModel/thicknessUnitHelpers'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  getNumberFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { parseNumber } from 'utils/parsingHelpers'
import {
  optionalNumberSchema,
  requiredNumberSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

export type TankFlatRoofAssetModelJSON = {
  name: string
  type: GeckoComponentModelType.TankFlatRoof
  radius: number
  features: Record<
    string,
    | GeckoVerticalWeldJSON
    | GeckoHorizontalWeldJSON
    | GeckoPlateJSON
    | GeckoNozzleJSON
    | GeckoManwayJSON
    | GeckoRectangularManwayJSON
  >
  scene_config: SceneConfig
  component_bounds: Record<string, number>
  tmin: number | undefined
  nominal: {
    base_thickness: number | undefined
  }
  compass_orientation: number | undefined
}

export const TankFlatRoofAvailableFeatures: GeckoFeatureModelType[] = [
  GeckoFeatureModelType.Plate,
  GeckoFeatureModelType.VerticalWeld,
  GeckoFeatureModelType.HorizontalWeld,
  GeckoFeatureModelType.Nozzle,
  GeckoFeatureModelType.Manway,
  GeckoFeatureModelType.RectangularManway,
  GeckoFeatureModelType.FixedSensor,
]

export const NewTankFlatRoofAssetModelJSON = {
  name: 'New Tank Roof',
  type: GeckoComponentModelType.TankFlatRoof,
  radius: 0,
  features: {},
  scene_config: {},
  component_bounds: {
    x_start: 0,
    y_start: 0,
    x_end: 0,
    y_end: 0,
  },
  tmin: undefined,
  nominal: {
    base_thickness: undefined,
  },
  compass_orientation: undefined,
} as TankFlatRoofAssetModelJSON

/**
 * A class representing a flat roof of a Rich Asset Model Tank
 */
export class GeckoTankFlatRoof extends GeckoEditableComponent {
  type: GeckoComponentModelType.TankFlatRoof
  radius: GeckoMeasurement
  componentBounds: GeckoComponentRect
  sceneConfig: SceneConfig
  plates: Map<string, GeckoPlate>
  horizontalWelds: Map<string, GeckoHorizontalWeld>
  verticalWelds: Map<string, GeckoVerticalWeld>
  nozzles: Map<string, GeckoNozzle>
  manways: Map<string, GeckoManway>
  rectangularManways: Map<string, GeckoRectangularManway>
  baseNominalThickness: GeckoMeasurement | undefined
  tmin: GeckoMeasurement | undefined
  compassOrientation: number | undefined
  private constructor(
    id: string,
    json: Record<string, unknown>,
    name: string,
    radius: GeckoMeasurement,
    componentBounds: GeckoComponentRect,
    sceneConfig: SceneConfig,
    plates: Map<string, GeckoPlate>,
    horizontalWelds: Map<string, GeckoHorizontalWeld>,
    verticalWelds: Map<string, GeckoVerticalWeld>,
    nozzles: Map<string, GeckoNozzle>,
    manways: Map<string, GeckoManway>,
    rectangularManways: Map<string, GeckoRectangularManway>,
    baseNominalThickness: GeckoMeasurement | undefined,
    tmin: GeckoMeasurement | undefined,
    compassOrientation: number | undefined
  ) {
    super(id, name, json)
    this.type = GeckoComponentModelType.TankFlatRoof
    this.radius = radius
    this.componentBounds = componentBounds
    this.sceneConfig = sceneConfig
    this.plates = plates
    this.verticalWelds = verticalWelds
    this.horizontalWelds = horizontalWelds
    this.nozzles = nozzles
    this.manways = manways
    this.rectangularManways = rectangularManways
    this.baseNominalThickness = baseNominalThickness
    this.tmin = tmin
    this.compassOrientation = compassOrientation
  }

  public get platesArray() {
    return Array.from(this.plates.values())
  }

  public get verticalWeldsArray() {
    return Array.from(this.verticalWelds.values())
  }

  public get horizontalWeldsArray() {
    return Array.from(this.horizontalWelds.values())
  }

  public get nozzlesArray() {
    return Array.from(this.nozzles.values())
  }

  public get manwaysArray() {
    return Array.from(this.manways.values())
  }

  public get rectangularManwaysArray() {
    return Array.from(this.rectangularManways.values())
  }

  public static CreateFromJSON(id: string, json: TankFlatRoofAssetModelJSON) {
    const name = json.name as string
    const radius = GeckoMeasurement.CreateFromValue(
      json.radius as number,
      GeckoMeasurementUnit.Meters
    )

    const componentBoundsData = json.component_bounds as Record<string, number>
    const componentBounds = GeckoComponentRect.CreateFromXYs(
      id,
      componentBoundsData.x_start,
      componentBoundsData.y_start,
      componentBoundsData.x_end,
      componentBoundsData.y_end,
      GeckoMeasurementUnit.Meters,
      GeckoMeasurementUnit.Meters
    )

    const sceneConfig = json.scene_config as SceneConfig

    const featureData = json.features as Record<
      string,
      | GeckoPlateJSON
      | GeckoHorizontalWeldJSON
      | GeckoVerticalWeldJSON
      | GeckoManwayJSON
      | GeckoNozzleJSON
      | GeckoRectangularManwayJSON
    >

    const childPlateJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.Plate
    })
    const plates = GeckoPlate.CreateMapFromJSON(
      id,
      childPlateJSON as Record<string, GeckoPlateJSON>
    )

    const childHorizontalWeldJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.HorizontalWeld
    })
    const horizontalWelds = GeckoHorizontalWeld.CreateMapFromJSON(
      id,
      childHorizontalWeldJSON as Record<string, GeckoHorizontalWeldJSON>
    )

    const childVerticalWeldJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.VerticalWeld
    })
    const verticalWelds = GeckoVerticalWeld.CreateMapFromJSON(
      id,
      childVerticalWeldJSON as Record<string, GeckoVerticalWeldJSON>
    )

    const childNozzleJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.Nozzle
    })
    const nozzles = GeckoNozzle.CreateMapFromJSON(
      id,
      childNozzleJSON as Record<string, GeckoNozzleJSON>
    )

    const childManwayJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.Manway
    })
    const manways = GeckoManway.CreateMapFromJSON(
      id,
      childManwayJSON as Record<string, GeckoManwayJSON>
    )

    const childRectangularManwayJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.RectangularManway
    })
    const rectangularManways = GeckoRectangularManway.CreateMapFromJSON(
      id,
      childRectangularManwayJSON as Record<string, GeckoRectangularManwayJSON>
    )

    const nominalJSON = json.nominal as Record<string, number>
    const baseNominalThicknessNum = parseNumber(nominalJSON.base_thickness)
    const baseNominalThickness =
      baseNominalThicknessNum !== undefined
        ? weightThicknessValue(
            GeckoMeasurement.CreateFromValue(
              baseNominalThicknessNum,
              GeckoMeasurementUnit.Meters
            )
          )
        : undefined
    const compassOrientation = parseNumber(json.compass_orientation)

    const parsedTmin = parseNumber(json.tmin)
    const tmin =
      parsedTmin !== undefined
        ? weightThicknessValue(
            GeckoMeasurement.CreateFromValue(
              parsedTmin,
              GeckoMeasurementUnit.Meters
            )
          )
        : undefined

    return new GeckoTankFlatRoof(
      id,
      json,
      name,
      radius,
      componentBounds,
      sceneConfig,
      plates,
      horizontalWelds,
      verticalWelds,
      nozzles,
      manways,
      rectangularManways,
      baseNominalThickness,
      tmin,
      compassOrientation
    )
  }

  public asJSON(): TankFlatRoofAssetModelJSON {
    return generateTankRoof(
      this.name,
      this.radius,
      this.baseNominalThickness,
      this.tmin,
      this.compassOrientation,
      this.json as TankFlatRoofAssetModelJSON
    )
  }
}

const generateTankRoof = (
  name: string,
  radius: GeckoMeasurement,
  baseNominalThickness: GeckoMeasurement | undefined,
  tmin: GeckoMeasurement | undefined,
  compassOrientation: number | undefined,
  otherTankRoofData: TankFlatRoofAssetModelJSON = NewTankFlatRoofAssetModelJSON
): TankFlatRoofAssetModelJSON => {
  const floorRadiusMeters = radius
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const floorDiameterMeters = radius
    .convertTo(GeckoMeasurementUnit.Meters)
    .scale(2)
    .toPrecision(assetModelPrecision).num

  const baseNominalThicknessMeters =
    baseNominalThickness !== undefined
      ? baseNominalThickness
          .convertTo(GeckoMeasurementUnit.Meters)
          ?.toPrecision(assetModelPrecision).num
      : undefined

  const tminMeters =
    tmin !== undefined
      ? tmin
          .convertTo(GeckoMeasurementUnit.Meters)
          ?.toPrecision(assetModelPrecision).num
      : undefined

  return {
    name: name,
    type: GeckoComponentModelType.TankFlatRoof,
    radius: floorRadiusMeters,
    features: otherTankRoofData.features,
    scene_config: otherTankRoofData.scene_config,
    component_bounds: {
      x_start: 0,
      y_start: 0,
      x_end: floorDiameterMeters,
      y_end: floorDiameterMeters,
    },
    tmin: tminMeters,
    nominal: { base_thickness: baseNominalThicknessMeters },
    compass_orientation: compassOrientation,
  }
}

export const TankRoofAssetModelFormFields = (
  tankRoofJSON: TankFlatRoofAssetModelJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Roof Name',
      value: tankRoofJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    // Diameter is the industry standard measurement for tanks, so convert to radius and back
    {
      dataKey: 'radius',
      label: 'Diameter',
      value: tankRoofJSON.radius * 2,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'base_thickness',
      label: 'Nominal Thickness',
      value: tankRoofJSON.nominal.base_thickness,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    tminFormField(tankRoofJSON.tmin),
    compassOrientationFormField(tankRoofJSON.compass_orientation),
  ]
}

export const TankRoofJSONFromFormFields = (
  component: TankFlatRoofAssetModelJSON,
  formFields: FormField[]
): TankFlatRoofAssetModelJSON => {
  const parsedBaseNominalThickness = parseNumber(
    getFormFieldByDataKey(formFields, 'base_thickness')?.value
  )
  const parsedTmin = parseNumber(
    getFormFieldByDataKey(formFields, 'tmin')?.value
  )
  const compassOrientation = getNumberFieldByDataKey(
    formFields,
    'compass_orientation'
  )
  return generateTankRoof(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      (getFormFieldByDataKey(formFields, 'radius')?.value as number) / 2, //radius is diameter
      GeckoMeasurementUnit.Meters
    ),
    parsedBaseNominalThickness !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedBaseNominalThickness,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    parsedTmin !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedTmin,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    compassOrientation,
    component
  )
}
